import React, { useState, useMemo } from 'react';
import { Box, Stack, Typography, Button, Alert, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';

import {
  DashboardCard,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  GranularityToggle,
} from '../../../../../shared/components';
import { buildFilterParams, useResolvedGranularity } from '../../shared/utils';
import { useClientScope } from '../../../../../shared/contexts';
import { usePersistedDateRange, usePersistedFilters } from '../../../../../shared/hooks';
import { useDepartments } from '../../../../../shared/components/Filters/hooks/useFilterOptions';
import { AiSummaryPanel, useAiSummary } from '../../../insights';

import {
  useComparisonDemographics,
  useComparisonHrv,
  useComparisonRecovery,
  useComparisonStrain,
  useComparisonSleep,
} from '../hooks';

import { DemographicsSection } from '../components/DemographicsSection';
import { HrvSection } from '../components/HrvSection';
import { RecoverySectionTop, RecoveryTrend } from '../components/RecoverySection';
import { StrainSectionTop, StrainTrend } from '../components/StrainSection';
import { SleepSectionTop, SleepTrend } from '../components/SleepSection';

import type { ProfileComparisonParams, SleepTrendMetric, RecoveryTrendMetric, StrainTrendMetric } from '../api/types';

export const EmployeeProfileComparison: React.FC = () => {
  const { effectiveClientId } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sleepMetric, setSleepMetric] = useState<SleepTrendMetric>('sleep_stages');
  const [recoveryMetric, setRecoveryMetric] = useState<RecoveryTrendMetric>('recovery_score');
  const [strainMetric, setStrainMetric] = useState<StrainTrendMetric>('day_strain');
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { granularityMode, setGranularityMode, granularity } = useResolvedGranularity(dateRange);

  const filterParams = useMemo(
    () => buildFilterParams(filters, dateRange, effectiveClientId),
    [filters, dateRange, effectiveClientId],
  );

  // The departments selected for comparison come from the department filter (IDs)
  const selectedDepartments = filters.department;

  // Resolve department IDs to names for display
  const { data: departmentsData } = useDepartments({ regionIds: filters.region });
  const deptNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (departmentsData?.data) {
      for (const dept of departmentsData.data) {
        map.set(dept.id, dept.name);
      }
    }
    return map;
  }, [departmentsData]);
  const getDeptName = (id: string) => deptNameMap.get(id) || id;

  const comparisonParams: ProfileComparisonParams = useMemo(
    () => ({
      ...filterParams,
      departments: selectedDepartments,
      granularity: granularity === 'hourly' ? 'daily' : granularity,
    }),
    [filterParams, selectedDepartments, granularity],
  );

  // Fetch data per section
  const demographics = useComparisonDemographics(comparisonParams);
  const hrv = useComparisonHrv(comparisonParams);
  const recovery = useComparisonRecovery(comparisonParams);
  const strain = useComparisonStrain(comparisonParams);
  const sleep = useComparisonSleep(comparisonParams);

  // AI Summary — build comparison groups from selected departments
  const aiGroups = useMemo(() => {
    return selectedDepartments.map((dept, index) => ({
      key: dept,
      label: deptNameMap.get(dept) || dept,
      color: `hsl(${(index * 360) / Math.max(selectedDepartments.length, 1)}, 70%, 50%)`,
      filterParams: { ...filterParams, department: [dept] },
    }));
  }, [selectedDepartments, filterParams, deptNameMap]);

  const { buildPayload: buildAiPayload, disabled: aiDisabled } = useAiSummary({
    dashboard: 'Employee Profile Comparison',
    groups: aiGroups,
    isComparing: selectedDepartments.length > 1,
    dimension: 'department',
    filterParams,
    metricSpecs: [
      { label: 'Recovery Score', keys: ['recovery_score'] },
      { label: 'Strain', keys: ['strain', 'day_strain'] },
      { label: 'HRV', keys: ['hrv', 'average_hrv'] },
      { label: 'Sleep Performance', keys: ['sleep_performance'] },
    ],
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Stack direction='row' spacing={2} alignItems='center'>
          <DurationSelector value={dateRange} onChange={setDateRange} />
          <GranularityToggle value={granularityMode} onChange={setGranularityMode} />
          <Button variant='outlined' startIcon={<FilterListIcon />} onClick={() => setIsFilterOpen(true)}>
            Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
          </Button>
        </Stack>
      </Box>

      {/* Empty state */}
      {selectedDepartments.length === 0 && (
        <Alert severity='info' sx={{ mb: 3 }}>
          Select departments from the filter to compare employee groups.
        </Alert>
      )}

      {/* Horizontally scrollable comparison columns */}
      {selectedDepartments.length > 0 && (
        <Box sx={{ overflowX: 'auto', pb: 2 }}>
          {/* CSS Grid: columns = departments, rows = sections. Each row aligns across all columns. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${selectedDepartments.length}, minmax(350px, 1fr))`,
              gridAutoRows: 'auto',
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {/* Row: Department headers */}
            {selectedDepartments.map((dept) => (
              <Box
                key={`header-${dept}`}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
              >
                <Typography variant='h6' sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {getDeptName(dept)}
                </Typography>
                <IconButton
                  size='small'
                  onClick={() => setFilters({ ...filters, department: filters.department.filter((d) => d !== dept) })}
                  aria-label={`Remove ${getDeptName(dept)}`}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: '50%', p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}

            {/* Row: Demographics */}
            {selectedDepartments.map((dept) => (
              <Box key={`demographics-${dept}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                <DemographicsSection
                  deptName={getDeptName(dept)}
                  demographics={demographics.data?.data.departments[dept]?.demographics}
                  workingHourPatterns={demographics.data?.data.departments[dept]?.working_hour_patterns}
                  ageDistribution={demographics.data?.data.departments[dept]?.age_distribution}
                  isLoading={demographics.isLoading}
                />
              </Box>
            ))}

            {/* Row: HRV */}
            {selectedDepartments.map((dept) => (
              <Box key={`hrv-${dept}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                <HrvSection
                  deptName={getDeptName(dept)}
                  stats={hrv.data?.data.departments[dept]?.stats}
                  trends={hrv.data?.data.departments[dept]?.trends}
                  isLoading={hrv.isLoading}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={{ ...filterParams, department: [dept] }}
                />
              </Box>
            ))}

            {/* Row: Recovery top (gauge + distribution) */}
            {selectedDepartments.map((dept) => (
              <Box key={`recovery-top-${dept}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                <RecoverySectionTop
                  distribution={recovery.data?.data.departments[dept]?.distribution}
                  isLoading={recovery.isLoading}
                  weatherFilters={{ ...filterParams, department: [dept] }}
                />
              </Box>
            ))}

            {/* Toggle: Recovery trends metric */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                position: 'sticky',
                left: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ToggleButtonGroup
                value={recoveryMetric}
                exclusive
                onChange={(_, val) => {
                  if (val) setRecoveryMetric(val);
                }}
                size='small'
              >
                <ToggleButton value='recovery_score'>Recovery Score</ToggleButton>
                <ToggleButton value='hrv'>Heart Rate Variability</ToggleButton>
                <ToggleButton value='resting_heart_rate'>Resting Heart Rate</ToggleButton>
                <ToggleButton value='sleep_performance'>Sleep Performance</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Row: Recovery trend chart */}
            {selectedDepartments.map((dept) => (
              <Box key={`recovery-trend-${dept}`}>
                <RecoveryTrend
                  deptName={getDeptName(dept)}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={{ ...filterParams, department: [dept] }}
                  selectedMetric={recoveryMetric}
                />
              </Box>
            ))}

            {/* Row: Strain top (gauge + zones) */}
            {selectedDepartments.map((dept) => (
              <Box key={`strain-top-${dept}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                <StrainSectionTop
                  heartRateZones={strain.data?.data.departments[dept]?.heart_rate_zones}
                  isLoading={strain.isLoading}
                  weatherFilters={{ ...filterParams, department: [dept] }}
                />
              </Box>
            ))}

            {/* Toggle: Strain trends metric */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                position: 'sticky',
                left: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ToggleButtonGroup
                value={strainMetric}
                exclusive
                onChange={(_, val) => {
                  if (val) setStrainMetric(val);
                }}
                size='small'
              >
                <ToggleButton value='day_strain'>Day Strain</ToggleButton>
                <ToggleButton value='cardio_load'>Cardio Load</ToggleButton>
                <ToggleButton value='heart_rate'>Heart Rate</ToggleButton>
                <ToggleButton value='calories_burn'>Calories Burned</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Row: Strain trend chart */}
            {selectedDepartments.map((dept) => (
              <Box key={`strain-trend-${dept}`}>
                <StrainTrend
                  deptName={getDeptName(dept)}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={{ ...filterParams, department: [dept] }}
                  selectedMetric={strainMetric}
                />
              </Box>
            ))}

            {/* Row: Sleep top (gauge + stages) */}
            {selectedDepartments.map((dept) => (
              <Box key={`sleep-top-${dept}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                <SleepSectionTop
                  sleepStages={sleep.data?.data.departments[dept]?.sleep_stages}
                  isLoading={sleep.isLoading}
                  weatherFilters={{ ...filterParams, department: [dept] }}
                />
              </Box>
            ))}

            {/* Toggle: Sleep trends metric */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                position: 'sticky',
                left: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ToggleButtonGroup
                value={sleepMetric}
                exclusive
                onChange={(_, val) => {
                  if (val) setSleepMetric(val);
                }}
                size='small'
              >
                <ToggleButton value='sleep_stages'>Stages</ToggleButton>
                <ToggleButton value='total_sleep'>Total Sleep</ToggleButton>
                <ToggleButton value='efficiency'>Efficiency</ToggleButton>
                <ToggleButton value='sleep_debt'>Sleep Debt</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Row: Sleep trend chart */}
            {selectedDepartments.map((dept) => (
              <Box key={`sleep-trend-${dept}`}>
                <SleepTrend
                  deptName={getDeptName(dept)}
                  trends={sleep.data?.data.departments[dept]?.trends}
                  clientId={effectiveClientId ?? null}
                  weatherFilters={{ ...filterParams, department: [dept] }}
                  selectedMetric={sleepMetric}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* AI Summary */}
      {selectedDepartments.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <DashboardCard title='AI Summary' expandable={false}>
            <AiSummaryPanel
              title={selectedDepartments.length > 1 ? 'Compare these groups with AI' : 'Summarise this group with AI'}
              buildPayload={buildAiPayload}
              disabled={aiDisabled || demographics.isLoading || hrv.isLoading}
            />
          </DashboardCard>
        </Box>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={(newFilters: FilterState) => {
          setFilters(newFilters);
          setIsFilterOpen(false);
        }}
        initialFilters={filters}
      />
    </Box>
  );
};
